import { useCallback, useEffect, useState } from 'react';
import { getFontScale } from '../../util';

/**
 * Breakpoints supported in our application.
 */
export enum Breakpoints {
  /** Mobile devices, small browsers */
  MOBILE = 768,
  /** Small browsers. */
  SMALL = 1024,
  /** medium sized browsers */
  MEDIUM = 1200,
  /** size between medium and large */
  MEDIUMBIG = 1400,
  /** Large browsers, but not quite 1080p */
  LARGE = 1600,
  /** Very small mobile devices, mobile devices with an active zoom. */
  MOBILE_ZOOM = 330
}

/**
 * Plain (non-hook) helper to synchronously check whether the current viewport matches a
 * breakpoint. Useful in contexts where {@link useBreakpoint} can't be used, since it isn't a hook
 * (e.g. inside event handlers or plain async functions).
 *
 * @param bp the breakpoint to check against
 * @return whether the current viewport width is at or below the given breakpoint
 */
export function matchesBreakpoint(bp: Breakpoints): boolean {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia('(max-width: ' + bp + 'px)').matches
  );
}

/**
 * Copied from react-laag
 *
 * MIT License
 * Copyright (c) 2019 Erik Verweij
 *
 */
function useBreakpointInternal(maxPixels: number): boolean {
  const [match, setMatch] = useState(matchesBreakpoint(maxPixels));
  useEffect(() => {
    const matcher = window.matchMedia('(max-width: ' + maxPixels + 'px)');

    function onMatch(evt: MediaQueryListEvent) {
      setMatch(evt.matches);
    }

    matcher.addEventListener('change', onMatch);
    return () => {
      matcher.removeEventListener('change', onMatch);
    };
  }, [maxPixels]);
  return match;
}

/**
 *
 */
export function useBreakpoint(bp: Breakpoints): boolean {
  return useBreakpointInternal(bp);
}

/**
 * Hook to determine if the device size is below a specific height in pixels.
 *
 * This is only meant for special cases where you need to react on a device's height.
 * In most normal circumstances, you should use {@link useBreakpoint}!
 */
export function useHeightBreakpoint(maxPixels: number): boolean {
  const [match, setMatch] = useState(
    typeof window !== 'undefined'
      ? window.matchMedia('(max-height: ' + maxPixels + 'px)').matches
      : false
  );
  useEffect(() => {
    const matcher = window.matchMedia('(max-height: ' + maxPixels + 'px)');

    function onMatch(evt: MediaQueryListEvent) {
      setMatch(evt.matches);
    }

    matcher.addEventListener('change', onMatch);
    return () => {
      matcher.removeEventListener('change', onMatch);
    };
  }, [maxPixels]);
  return match;
}

/**
 * Hook to determine whether the font zoom is above a specific breakpoint.
 *
 * @param breakpoint  breakpoint in percentage (e.g. 1.5 is 150%)
 */
export function useFontZoomBreakpoint(breakpoint: number): boolean {
  const compute = useCallback(() => getFontScale(), []);
  const [zoom, setZoom] = useState(compute());

  useEffect(() => {
    const update = () => setZoom(compute());

    // ResizeObserver to observe most changes, including computed font-size changes
    let rootEl = document.getElementById('content');
    if (!rootEl) {
      rootEl = document.getElementById('storybook-root');
    }
    let resizeObserver: ResizeObserver | null = null;

    if (rootEl) {
      resizeObserver = new ResizeObserver(update);
      resizeObserver.observe(rootEl);
    }

    // devicePixelRatio changes on zoom or display changes
    const mq = window.matchMedia(
      `(resolution: ${window.devicePixelRatio}dppx)`
    );
    mq.addEventListener('change', update);

    // fallback: resize event also catches some zoom changes
    window.addEventListener('resize', update);

    return () => {
      resizeObserver?.disconnect();
      mq.removeEventListener('change', update);
      window.removeEventListener('resize', update);
    };
  }, [compute]);

  return zoom >= breakpoint;
}

/**
 * Hook, which combines {@link useZoomBreakpoint} and {@link useBreakpoint} to easily make visual changes depending on font zoom or the device width.
 *
 * @param fontBreakpoint font zoom breakpoint in percentage (e.g. 1.5 is 150%)
 * @param widthBreakpoint width {@link Breakpoints}. Default is {@link Breakpoints.MOBILE_ZOOM}
 * @return `true` if either the fontBreakpoint or widthBreakpoint has been reached. Otherwise, `false`.
 */
export function useZoomBreakpoint(
  fontBreakpoint: number,
  widthBreakpoint = Breakpoints.MOBILE_ZOOM
): boolean {
  const zoom = useFontZoomBreakpoint(fontBreakpoint);
  const breakpoint = useBreakpoint(widthBreakpoint);
  return zoom || breakpoint;
}
